import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Agent, AgentFilters, CreateAgentData } from '../types';
import { ApiClient } from '@utils/apiClient';
import { handleApiError, ProcessedErrors } from '@utils/errorHandler';

export const useAgents = () => {
  const { t } = useTranslation();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AgentFilters>({
    search: '',
    status: '',
    language: '',
    voice: '',
  });

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiClient.get('/agents/agents/');

      if (!response.ok) {
        throw new Error(`Failed to fetch agents: ${response.status} ${response.statusText}`);
      }

      const agentsData: Agent[] = await response.json();
      setAgents(agentsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  }, []);

  const createAgent = async (
    agentData: CreateAgentData
  ): Promise<{ success: boolean; agent?: Agent; errors?: ProcessedErrors }> => {
    try {
      const response = await ApiClient.post('/agents/agents/', agentData);

      if (!response.ok) {
        const processedErrors = await handleApiError(response, t);
        return {
          success: false,
          errors: processedErrors,
        };
      }

      const responseData = await response.json();

      // Check if the response contains errors even with 200 status
      if (responseData.field_errors || responseData.non_field_errors || responseData.detail) {
        const processedErrors = await handleApiError(
          {
            status: response.status,
            json: () => Promise.resolve(responseData),
          } as Response,
          t
        );
        return {
          success: false,
          errors: processedErrors,
        };
      }

      const createdAgent: Agent = responseData;

      // Refresh the agents list
      await fetchAgents();

      return {
        success: true,
        agent: createdAgent,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create agent';
      setError(errorMessage);
      return {
        success: false,
        errors: {
          fieldErrors: {},
          nonFieldErrors: [],
          generalError: errorMessage,
        },
      };
    }
  };

  const updateAgent = async (
    id: string,
    agentData: Partial<CreateAgentData>
  ): Promise<{ success: boolean; errors?: ProcessedErrors }> => {
    try {
      const response = await ApiClient.patch(`/agents/agents/${id}/`, agentData);

      if (!response.ok) {
        const processedErrors = await handleApiError(response, t);
        return {
          success: false,
          errors: processedErrors,
        };
      }

      // Refresh the agents list
      await fetchAgents();
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update agent';
      setError(errorMessage);
      return {
        success: false,
        errors: {
          fieldErrors: {},
          nonFieldErrors: [],
          generalError: errorMessage,
        },
      };
    }
  };

  const deleteAgent = async (id: string): Promise<boolean> => {
    try {
      const response = await ApiClient.delete(`/agents/agents/${id}/`);

      if (!response.ok) {
        throw new Error(`Failed to delete agent: ${response.status} ${response.statusText}`);
      }

      // Refresh the agents list
      await fetchAgents();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete agent');
      return false;
    }
  };

  const toggleAgentStatus = async (id: string): Promise<boolean> => {
    try {
      const agent = agents.find(a => a.id === id);
      if (!agent) return false;

      // Toggle the status
      const newStatus = agent.status === 'active' ? 'inactive' : 'active';
      const response = await ApiClient.patch(`/agents/agents/${id}/`, { status: newStatus });

      if (!response.ok) {
        throw new Error(`Failed to toggle agent status: ${response.status} ${response.statusText}`);
      }

      // Update local state instead of refetching
      setAgents(prevAgents =>
        prevAgents.map(agent =>
          agent.id === id
            ? { ...agent, status: newStatus, is_active: newStatus === 'active' }
            : agent
        )
      );

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle agent status');
      return false;
    }
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch =
      !filters.search ||
      agent.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      agent.intro_message.toLowerCase().includes(filters.search.toLowerCase());

    const matchesStatus = !filters.status || agent.status === filters.status;
    const matchesLanguage = !filters.language || agent.language.code === filters.language;
    const matchesVoice = !filters.voice || (agent.voice && agent.voice.id === filters.voice);

    return matchesSearch && matchesStatus && matchesLanguage && matchesVoice;
  });

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const fetchAgent = useCallback(async (id: string): Promise<Agent | null> => {
    try {
      const response = await ApiClient.get(`/agents/agents/${id}/`);

      if (!response.ok) {
        throw new Error(`Failed to fetch agent: ${response.status} ${response.statusText}`);
      }

      const agentData: Agent = await response.json();
      return agentData;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch agent');
      return null;
    }
  }, []);

  return {
    agents: filteredAgents,
    loading,
    error,
    filters,
    setFilters,
    createAgent,
    updateAgent,
    deleteAgent,
    toggleAgentStatus,
    fetchAgent,
    refetch: fetchAgents,
  };
};
